<template>
    <div class="chart-container">
        <div class="chart-header">
            <h3 class="titleGraph">{{ title }}</h3>
            <b-icon-gear-fill class="settings-button" @click="openSettingsModal" />
        </div>

        <v-chart ref="chart" :option="chartOptions" :theme="defaultTheme" style="height: 100%; width: 100%;" />

        <!-- Modal de Configuração -->
        <div v-if="showModal" class="modal">
            <div class="modal-content">
                <h3>Configurações</h3>

                <!-- Opção de Valores Absolutos/Percentuais -->
                <div class="settings-group">
                    <p>Exibição dos Valores</p>
                    <label>
                        <input type="radio" v-model="showPercentage" :value="false" /> Absoluto
                    </label>
                    <label>
                        <input type="radio" v-model="showPercentage" :value="true" /> Porcentagem
                    </label>
                </div>

                <!-- Filtro de Dados -->
                <div class="settings-group">
                    <p>Filtrar Dados</p>
                    <input type="text" v-model="filterText" placeholder="Filtrar" />
                    <div class="checkbox-list">
                        <label v-for="(item, index) in filteredLabels" :key="index">
                            <input type="checkbox" v-model="selectedFilters" :value="item" />
                            {{ item }}
                        </label>
                    </div>
                </div>

                <!-- Botões -->
                 <div class="modalBtn">
                     <button @click="closeSettingsModal">Fechar</button>
                     <button @click="applySettings">Confirmar</button>
                 </div>
            </div>
        </div>
    </div>
</template>

<script>
import VChart from "vue-echarts";
import * as echarts from 'echarts';
import "echarts";
import 'echarts/lib/chart/bar';
import 'echarts/lib/chart/line';
import 'echarts/lib/component/tooltip';
import 'echarts/lib/component/legend';
import 'echarts/lib/component/title';
import CustomTheme from './graphTheme.json'

export default {
    name: "RTAGraph",
    components: { VChart },
    props: {
        type: { type: String, required: true },
        title: { type: String, required: true },
        labels: { type: Array, required: true },
        data: { type: Array, required: true },
        categories: { type: Array, required: false, default: () => [] },
        graphSize: { type: Number, required: false, default: 150 }
    },
    data() {
        return {
            defaultTheme: 'customTheme',
            showPercentage: false,
            showModal: false,
            selectedFilters: [], // Será preenchido dinamicamente
            filterText: "",
        };
    },
    watch: {
        labels: {
            immediate: true, // Chama a função imediatamente ao carregar os dados
            handler(newLabels) {
                this.selectedFilters = [...newLabels]; // Mantém todos os labels ativos
            }
        }
    },
    computed: {
        filteredLabels() {
            return this.labels.filter((label) =>
                label.toLowerCase().includes(this.filterText.toLowerCase())
            );
        },
        filteredData() {
            // Se não houver filtros aplicados, retornar todos os dados
            if (this.selectedFilters.length === 0) {
                return this.data;
            }

            // Caso contrário, filtrar os dados normalmente
            return this.data.filter((_, index) =>
                this.selectedFilters.includes(this.labels[index])
            );
        },
        chartOptions() {
            if (this.type === "bar" && this.categories.length > 0) {
                let seriesData = this.categories.map((category, catIndex) => ({
                    name: category,
                    type: "bar",
                    data: this.filteredData.map((values) => {
                        let value = values[catIndex] ?? 0; // Garante que não fique undefined
                        return this.showPercentage ? ((value / this.totalValue) * 100).toFixed(2) : Number(value).toFixed(2);
                    }),
                }));

                return {
                    tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
                    legend: {
                        bottom: 0, data: this.categories, textStyle: {
                            fontSize: 10,
                        },
                        icon: "circle",
                        itemWidth: 8,
                        itemHeight: 8,
                    },
                    grid: {
                        top: 20,
                        bottom: 70, // Aumenta espaço entre os dados e a legenda
                        left: 40,
                        right: 20
                    },
                    xAxis: {
                        type: "category", data: this.selectedFilters, axisLabel: {
                            fontSize: 10,
                            interval: 0,
                        }
                    },
                    yAxis: { type: "value" },
                    series: seriesData.map(series => ({
                        ...series,
                        label: {
                            show: true,
                            fontSize: 10,
                        },
                    }))
                };
            }

            if (this.type === "line") {
                let seriesData = this.categories.map((category, catIndex) => ({
                    name: category,
                    type: "line",
                    data: this.filteredData.map((values) => {
                        let value = values[catIndex];
                        return this.showPercentage ? ((value / this.totalValue) * 100).toFixed(2) : Number(value).toFixed();
                    }),
                    smooth: true,
                }));

                return {
                    tooltip: { trigger: "axis" },
                    legend: {
                        bottom: 0, data: this.categories, textStyle: {
                            fontSize: 10,
                        },
                        itemWidth: 8,
                        itemHeight: 8,
                    },
                    grid: { top: 10, bottom: 50, left: 40, right: 20 },
                    xAxis: {
                        type: "category", data: this.selectedFilters, axisLabel: {
                            fontSize: 10,
                            interval: 0,
                        }
                    },
                    yAxis: { type: "value" },
                    series: seriesData.map(series => ({
                        ...series,
                        label: {
                            show: true,
                            fontSize: 10,
                        },
                    })),
                };
            }

            if (this.type === "pie") {
                return {
                    tooltip: { trigger: "item" },
                    legend: {
                        orient: "vertical", // Define a orientação vertical
                        right: 0, // Move para a lateral direita
                        top: "center",
                        textStyle: {
                            fontSize: 10,
                        },
                        icon: "circle",
                        itemWidth: 8,
                        itemHeight: 8,
                    },
                    series: [
                        {
                            name: this.title,
                            type: "pie",
                            radius: "60%", // Aumenta o tamanho do gráfico
                            center: ["40%", "50%"], // Move o gráfico mais para a esquerda para abrir espaço para a legenda
                            data: this.filteredData.map((value, index) => ({
                                name: this.selectedFilters[index],
                                value: this.showPercentage ? ((value / this.totalValue) * 100).toFixed(2) : Number(value).toFixed(2),
                            })),
                        },
                    ],
                };
            }

            return { title: { text: "Gráfico Inválido" }, series: [] };
        },
        totalValue() {
            return this.data.flat().reduce((sum, val) => (sum + val), 0).toFixed(2);
        },
    },
    methods: {
        openSettingsModal() {
            this.showModal = true;
        },
        closeSettingsModal() {
            this.showModal = false;
        },
        applySettings() {
            this.closeSettingsModal();
        },

        resizeChart() {
            this.$nextTick(() => {
                if (this.$refs.chart) {
                    setTimeout(() => this.$refs.chart.resize(), 300);
                }
            });
        }
    },
    created() {
        echarts.registerTheme('customTheme', CustomTheme);
    },
    mounted() {
        this.resizeChart();
        window.addEventListener('resize', this.resizeChart);
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.resizeChart);
    },
};
</script>

<style scoped>
.titleGraph {
    font-size: 1rem;
    font-weight: 600;
    margin: 0;
    padding: 0;
}

.chart-container {
    background: white;
    border-radius: 0.2rem;
    padding: 8px;
    box-shadow: 0 2px 5px #00000033;
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
}

.chart-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 0.3rem;
}

.settings-button {
    cursor: pointer;
    color: #7E7E7E;
    margin: none;
    padding: none;
}

.modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
}

.modal-content {
    background: white;
    padding: 20px;
    border-radius: 10px;
    width: 350px;
}

.modal .modalBtn{
    display: flex;
    align-items: center;
    column-gap: 0.3rem;
}

.modal .modalBtn button:first-child{
    background-color: #fff;
    color: #008787;
}

.settings-group {
    margin-bottom: 15px;
    display: flex;
    flex-direction: column;
    row-gap: 0.3rem;
}

.checkbox-list {
    display: flex;
    flex-direction: column;
    margin: 0.2rem 0;
    max-height: 6.5rem;
    overflow-y: auto;
}

button {
    margin-top: 10px;
    padding: 5px 10px;
    border: none;
    background: #008787;
    border: 1px solid #008787;
    width: 100%;
    color: white;
    cursor: pointer;
    border-radius: 0.2rem;
    font-weight: 500;
}
</style>